import apiClient from './api.service';
import { Product, CreateProductData, UpdateProductData, ProductFilters, ProductsResponse, AxisDecision, LinkPreview, SyncPreview, StoreListing } from '../types/product.types';
import { traceProductsHttp } from '../utils/productsDebug';

/** Une seule requête HTTP en vol par clé : évite les rafales si plusieurs effets appellent la même liste en parallèle. */
const pendingListByKey = new Map<string, Promise<ProductsResponse>>();
const pendingDetailByKey = new Map<string, Promise<Product>>();

function listRequestKey(storeId: number, filters: ProductFilters, page: number, limit: number): string {
  const f = filters || {};
  return `${storeId}|p${page}|l${limit}|s:${f.search ?? ''}|a:${f.is_active ?? ''}|src:${f.source ?? ''}`;
}

function detailRequestKey(storeId: number, productId: number): string {
  return `${storeId}:${productId}`;
}

/**
 * Product Service
 * Handles all product-related API calls
 */
class ProductService {
  /**
   * Get all products for a store with filters
   * @param storeId - Store ID
   * @param filters - Filter options
   * @param page - Page number
   * @param limit - Items per page
   * @returns Promise with products and pagination
   */
  async getProducts(storeId: number, filters: ProductFilters = {}, page: number = 1, limit: number = 20): Promise<ProductsResponse> {
    const key = listRequestKey(storeId, filters, page, limit);
    const existing = pendingListByKey.get(key);
    if (existing) {
      return existing;
    }

    const promise = (async (): Promise<ProductsResponse> => {
      try {
        traceProductsHttp('HTTP GET liste produits', { storeId, page, limit, filters });
        const params: any = { page, limit };

        if (filters.search) params.search = filters.search;
        if (filters.is_active !== undefined) params.is_active = filters.is_active.toString();
        if (filters.source) params.source = filters.source;

        const response = await apiClient.get(`/stores/${storeId}/products`, { params });
        return {
          success: true,
          data: response.data.data,
          pagination: response.data.pagination
        };
      } finally {
        pendingListByKey.delete(key);
      }
    })();

    pendingListByKey.set(key, promise);
    return promise;
  }

  /**
   * Get a specific product
   * @param storeId - Store ID
   * @param productId - Product ID
   * @returns Promise with product data
   */
  async getProductById(storeId: number, productId: number): Promise<Product> {
    const key = detailRequestKey(storeId, productId);
    const existing = pendingDetailByKey.get(key);
    if (existing) {
      return existing;
    }

    const promise = (async (): Promise<Product> => {
      try {
        // Use longer timeout (20s) for product detail - backend can be slow (e.g. cold start)
        const response = await apiClient.get(`/stores/${storeId}/products/${productId}`, { timeout: 20000 });
        return response.data.data;
      } finally {
        pendingDetailByKey.delete(key);
      }
    })();

    pendingDetailByKey.set(key, promise);
    return promise;
  }

  /**
   * Create a new product
   * @param storeId - Store ID
   * @param data - Product data
   * @returns Promise with created product
   */
  async createProduct(storeId: number, data: CreateProductData): Promise<Product> {
    const response = await apiClient.post(`/stores/${storeId}/products`, data);
    return response.data.data;
  }

  /**
   * Update a product
   * @param storeId - Store ID
   * @param productId - Product ID
   * @param data - Updated product data
   * @returns Promise with updated product
   */
  async updateProduct(storeId: number, productId: number, data: UpdateProductData): Promise<Product> {
    const response = await apiClient.put(`/stores/${storeId}/products/${productId}`, data);
    return response.data.data;
  }

  async getListings(
    storeId: number,
    filters: { search?: string; grouped?: boolean } = {},
    page = 1,
    limit = 20
  ): Promise<{ data: StoreListing[]; pagination: ProductsResponse['pagination'] }> {
    const params: Record<string, string | number> = { page, limit };
    if (filters.search) params.search = filters.search;
    if (filters.grouped) params.grouped = 'true';
    const response = await apiClient.get(`/stores/${storeId}/products/listings`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  }

  /**
   * Delete a product
   * @param storeId - Store ID
   * @param productId - Product ID
   * @returns Promise
   */
  async deleteProduct(storeId: number, productId: number): Promise<void> {
    await apiClient.delete(`/stores/${storeId}/products/${productId}`);
  }

  async previewLink(
    storeId: number,
    keeperId: number,
    absorbIds: number[],
    axisDecisions: AxisDecision[] = []
  ): Promise<LinkPreview> {
    const response = await apiClient.post(`/stores/${storeId}/products/link/preview`, {
      keeperId,
      absorbIds,
      axisDecisions
    });
    return response.data.data;
  }

  async applyLink(
    storeId: number,
    keeperId: number,
    absorbIds: number[],
    axisDecisions: AxisDecision[] = [],
    imageMode: 'keep' | 'merge' = 'keep'
  ): Promise<void> {
    await apiClient.post(`/stores/${storeId}/products/link`, {
      keeperId,
      absorbIds,
      axisDecisions,
      imageMode
    });
  }

  async uploadImages(storeId: number, files: File[]): Promise<string[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const response = await apiClient.post(`/stores/${storeId}/products/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    });
    return response.data.data.urls || [];
  }

  async unlinkListing(storeId: number, productId: number, listingId: number): Promise<void> {
    await apiClient.post(`/stores/${storeId}/products/${productId}/listings/${listingId}/unlink`);
  }

  async previewSync(
    storeId: number,
    productId: number,
    listingIds: number[],
    axisDecisions: AxisDecision[] = []
  ): Promise<SyncPreview> {
    const response = await apiClient.post(`/stores/${storeId}/products/${productId}/sync/preview`, {
      listingIds,
      axisDecisions
    });
    return response.data.data;
  }

  async applySync(
    storeId: number,
    productId: number,
    payload: {
      listingIds: number[];
      mode: 'add' | 'overwrite';
      applyCatalogFields: boolean;
      axisDecisions: AxisDecision[];
      removalFingerprint: string;
    }
  ): Promise<void> {
    await apiClient.post(`/stores/${storeId}/products/${productId}/sync`, payload);
  }
}

export default new ProductService();

